/**
 * Centralized theme helpers.
 *
 * IMPORTANT: the TradingView charting library OWNS `tradingview.current_theme.name`
 * in localStorage and force-flushes the chart's *current* theme into it — including
 * when a saved chart layout is loaded. That makes it unsafe as our source of truth:
 * loading a stale layout (e.g. one saved as light) clobbers the key back to light
 * and loses the user's choice on a quick reload.
 *
 * So we keep our OWN key (`APP_THEME_STORAGE_KEY`) that the library never writes,
 * and treat it as the single source of truth for this browser. We mirror the value
 * into the library key too (so the chart's own init path starts from it), but we
 * never TRUST the library key — we only read it once as a migration seed.
 *
 * The server preference (`chartTheme`) is used only for background cross-device
 * sync and as a first-visit bootstrap (when this browser has no stored theme).
 */

export type ChartTheme = "light" | "dark";

/** Our own, authoritative theme key. The charting library never writes this. */
export const APP_THEME_STORAGE_KEY = "rupeezy.chart.theme";

/** Key the TradingView library owns/overwrites. Used only as a migration seed. */
export const LIBRARY_THEME_STORAGE_KEY = "tradingview.current_theme.name";

/**
 * Signature `{ id, modified, theme }` of the layout this browser last painted.
 * The library bakes theme colors into the shared layout, and `getTheme()` follows
 * our local name not those colors, so we detect a stale layout (re-saved by another
 * surface, or our theme changed) by comparing this against the loaded record.
 * Subsumes the old "dirty" flag.
 */
export const LAYOUT_SIGNATURE_STORAGE_KEY = "rupeezy.chart.layout.sig";

/** CSS class applied to `#themeManage` to switch the React interface to dark. */
export const DARK_THEME_CLASS = "DarkTheme";

/** Id of the wrapper element whose class drives the interface theme. */
export const THEME_ROOT_ID = "themeManage";

const DEFAULT_THEME: ChartTheme = "light";

/** Coerce any value into a valid `ChartTheme`, defaulting to light. */
export function normalizeTheme(value: unknown): ChartTheme {
	return String(value ?? "").toLowerCase() === "dark" ? "dark" : "light";
}

function readThemeKey(key: string): ChartTheme | null {
	try {
		const value = localStorage.getItem(key);
		return value == null ? null : normalizeTheme(value);
	} catch {
		return null;
	}
}

/**
 * Read the theme stored for this browser from OUR key. Falls back to the library
 * key once (migration) so existing users keep their theme on the first load after
 * this change. Returns `null` only when neither key has a value (true first visit),
 * which callers use to decide whether to bootstrap from the server preference.
 */
export function getStoredTheme(): ChartTheme | null {
	return readThemeKey(APP_THEME_STORAGE_KEY) ?? readThemeKey(LIBRARY_THEME_STORAGE_KEY);
}

/** Theme to use right now, falling back to the default when nothing is stored. */
export function getActiveTheme(): ChartTheme {
	return getStoredTheme() ?? DEFAULT_THEME;
}

/**
 * Persist the theme. We write OUR key (the source of truth) and mirror it into the
 * library key so the chart's own init path also starts from the right value.
 */
export function setStoredTheme(theme: ChartTheme): void {
	try {
		localStorage.setItem(APP_THEME_STORAGE_KEY, theme);
		localStorage.setItem(LIBRARY_THEME_STORAGE_KEY, theme);
	} catch {
		/* storage unavailable (private mode etc.) — non-fatal */
	}
}

/** `modified` is the layout's last-save stamp (adapter `timestamp` epoch ms, or
 *  `modified_iso`); `null` when unknown. */
export interface LayoutThemeSignature {
	id: string;
	modified: number | null;
	theme: ChartTheme;
}

export interface LayoutRecordRef {
	id: string;
	modified: number | null;
}

/** Read the signature of the layout this browser last painted, or `null`. */
export function readLayoutSignature(): LayoutThemeSignature | null {
	try {
		const raw = localStorage.getItem(LAYOUT_SIGNATURE_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object") return null;
		return {
			id: String(parsed.id ?? ""),
			modified: typeof parsed.modified === "number" ? parsed.modified : null,
			theme: normalizeTheme(parsed.theme),
		};
	} catch {
		return null;
	}
}

/** Record what we just painted so the next load of the same layout is a no-op. */
export function writeLayoutSignature(signature: LayoutThemeSignature): void {
	try {
		localStorage.setItem(
			LAYOUT_SIGNATURE_STORAGE_KEY,
			JSON.stringify({
				id: String(signature.id ?? ""),
				modified: signature.modified ?? null,
				theme: signature.theme,
			}),
		);
	} catch {
		/* storage unavailable — non-fatal */
	}
}

/** True when the chart must be force-repainted to `desired`: nothing painted yet,
 *  the loaded layout differs from what we recorded, or our theme changed since. */
export function layoutNeedsRepaint(record: LayoutRecordRef, desired: ChartTheme): boolean {
	const signature = readLayoutSignature();
	if (!signature) return true;
	if (signature.theme !== desired) return true;
	if (signature.id !== String(record?.id ?? "")) return true;
	return (signature.modified ?? null) !== (record?.modified ?? null);
}

/** Class name for the interface root that matches the stored theme. */
export function getInterfaceThemeClassName(): string {
	return getActiveTheme() === "dark" ? DARK_THEME_CLASS : "";
}

/**
 * Apply the theme to the React interface by toggling `DarkTheme` on the wrapper.
 * Safe to call before the element exists; it simply no-ops in that case.
 */
export function applyInterfaceTheme(theme: ChartTheme): void {
	const element = document.getElementById(THEME_ROOT_ID);
	if (!element) return;
	element.classList.toggle(DARK_THEME_CLASS, theme === "dark");
}
